import cn from 'classnames';
import {
    ChangeEvent,
    forwardRef,
    KeyboardEvent,
    MouseEvent,
    Ref,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { LoadingSpinner } from '@components/uiKit/LoadingSpinner';

import { createId } from '@scripts/createId';
import { useOnClickOutside } from '@scripts/hooks';

import CloseIcon from '@icons/16/close.svg';
import ArrowDropDownIcon from '@icons/24/arrow-drop-down.svg';
import ErrorCircleIcon from '@icons/error-circle.svg';

import { SELECT_ID_LENGTH, sizeClass } from './constants';
import styles from './Select.module.scss';
import { SelectOption, SelectProps } from './types';

const getOptionLabel = (option: SelectOption) => {
    if (typeof option.label === 'string' || typeof option.label === 'number') {
        return String(option.label);
    }

    return '';
};

const isSameOptionValue = (left: string | number, right: string | number) => String(left) === String(right);

const Select = forwardRef<HTMLButtonElement | HTMLInputElement, SelectProps>(
    (
        {
            id,
            className,
            customCss,
            size = 'lg',
            label,
            hint,
            error,
            loading = false,
            disabled = false,
            clearable = false,
            required,
            placeholder = '',
            searchable = false,
            emptySearchText = 'Ничего не найдено',
            options,
            value,
            defaultValue,
            name,
            field,
            helpers,
            onChange,
            onClear,
        },
        ref
    ) => {
        const generatedIdRef = useRef<string>(`select-${createId(SELECT_ID_LENGTH)}`);
        const selectId = id || generatedIdRef.current;
        const listboxId = `${selectId}-listbox`;
        const messageId = `${selectId}-message`;
        const containerRef = useRef<HTMLDivElement>(null);
        const fieldName = name || field?.name;

        const isControlled = value !== undefined || field !== undefined;
        const [internalValue, setInternalValue] = useState(defaultValue || '');
        const [isOpen, setIsOpen] = useState(false);
        const [activeIndex, setActiveIndex] = useState(-1);
        const [inputText, setInputText] = useState('');

        const selectedValue = isControlled ? (value ?? field?.value ?? '') : internalValue;

        const selectedOption = useMemo(
            () => options.find(option => isSameOptionValue(option.value, selectedValue)),
            [options, selectedValue]
        );

        const filteredOptions = useMemo(() => {
            if (!searchable) {
                return options;
            }

            const normalizedQuery = inputText.trim().toLowerCase();

            if (!normalizedQuery) {
                return options;
            }

            return options.filter(option => getOptionLabel(option).toLowerCase().includes(normalizedQuery));
        }, [inputText, options, searchable]);

        const enabledOptions = useMemo(() => filteredOptions.filter(option => !option.disabled), [filteredOptions]);

        const hasError = Boolean(error);
        const errorText = typeof error === 'string' ? error : '';
        const messageText = hasError ? errorText : hint || '';
        const hasSelectedValue = selectedOption !== undefined;
        const showClearButton = clearable && hasSelectedValue && !loading && !disabled;

        const closeDropdown = useCallback(() => {
            setIsOpen(false);
            setActiveIndex(-1);
            setInputText(selectedOption ? getOptionLabel(selectedOption) : '');
        }, [selectedOption]);

        const openDropdown = useCallback(() => {
            if (disabled || loading) {
                return;
            }

            setIsOpen(true);

            const selectedIndex = enabledOptions.findIndex(option => isSameOptionValue(option.value, selectedValue));
            setActiveIndex(selectedIndex >= 0 ? selectedIndex : enabledOptions.length > 0 ? 0 : -1);
        }, [disabled, enabledOptions, loading, selectedValue]);

        const handleSelect = useCallback(
            (option: SelectOption) => {
                if (option.disabled) {
                    return;
                }

                const nextValue = option.value;

                if (!isControlled) {
                    setInternalValue(nextValue);
                }

                field?.onChange({
                    target: {
                        name: field.name,
                        value: nextValue,
                    },
                });

                setInputText(getOptionLabel(option));
                onChange?.(nextValue);
                setIsOpen(false);
                setActiveIndex(-1);
            },
            [field, isControlled, onChange]
        );

        const handleToggle = useCallback(() => {
            if (isOpen) {
                closeDropdown();

                return;
            }

            openDropdown();
        }, [closeDropdown, isOpen, openDropdown]);

        const handleListNavigation = useCallback(
            (event: KeyboardEvent<HTMLButtonElement | HTMLInputElement>) => {
                if (event.key === 'Escape') {
                    event.preventDefault();
                    closeDropdown();

                    return;
                }

                if (event.key === 'ArrowDown') {
                    event.preventDefault();

                    if (!isOpen) {
                        openDropdown();

                        return;
                    }

                    setActiveIndex(currentIndex => {
                        if (enabledOptions.length === 0) {
                            return -1;
                        }

                        const nextIndex = currentIndex + 1;

                        return nextIndex >= enabledOptions.length ? 0 : nextIndex;
                    });

                    return;
                }

                if (event.key === 'ArrowUp') {
                    event.preventDefault();

                    if (!isOpen) {
                        openDropdown();

                        return;
                    }

                    setActiveIndex(currentIndex => {
                        if (enabledOptions.length === 0) {
                            return -1;
                        }

                        const nextIndex = currentIndex - 1;

                        return nextIndex < 0 ? enabledOptions.length - 1 : nextIndex;
                    });

                    return;
                }

                if (event.key === 'Enter' && isOpen) {
                    event.preventDefault();
                    const option = enabledOptions[activeIndex];

                    if (option) {
                        handleSelect(option);
                    }
                }
            },
            [activeIndex, closeDropdown, enabledOptions, handleSelect, isOpen, openDropdown]
        );

        const handleTriggerKeyDown = useCallback(
            (event: KeyboardEvent<HTMLButtonElement>) => {
                if (disabled || loading) {
                    return;
                }

                if (!isOpen) {
                    if (
                        event.key === 'ArrowDown' ||
                        event.key === 'ArrowUp' ||
                        event.key === 'Enter' ||
                        event.key === ' '
                    ) {
                        event.preventDefault();
                        openDropdown();
                    }

                    return;
                }

                if (event.key === ' ') {
                    event.preventDefault();
                }

                handleListNavigation(event);
            },
            [disabled, handleListNavigation, isOpen, loading, openDropdown]
        );

        const handleInputChange = useCallback(
            (event: ChangeEvent<HTMLInputElement>) => {
                setInputText(event.target.value);

                if (!isOpen) {
                    openDropdown();
                }
            },
            [isOpen, openDropdown]
        );

        const handleInputFocus = useCallback(() => {
            openDropdown();
        }, [openDropdown]);

        const handleInputKeyDown = useCallback(
            (event: KeyboardEvent<HTMLInputElement>) => {
                if (disabled || loading) {
                    return;
                }

                handleListNavigation(event);
            },
            [disabled, handleListNavigation, loading]
        );

        const handleChevronMouseDown = useCallback((event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
        }, []);

        const handleClearMouseDown = useCallback((event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
        }, []);

        const handleClear = useCallback(
            (event: MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();

                if (!isControlled) {
                    setInternalValue('');
                }

                if (helpers) {
                    void helpers.setValue('');
                } else {
                    field?.onChange({
                        target: {
                            name: field.name,
                            value: '',
                        },
                    });
                }

                setInputText('');
                onChange?.('');
                onClear?.();
                setIsOpen(false);
                setActiveIndex(-1);
            },
            [field, helpers, isControlled, onChange, onClear]
        );

        useOnClickOutside(containerRef, () => {
            if (isOpen) {
                closeDropdown();
            }
        });

        useEffect(() => {
            if (isOpen) {
                return;
            }

            setInputText(selectedOption ? getOptionLabel(selectedOption) : '');
        }, [isOpen, selectedOption, selectedValue]);

        useEffect(() => {
            if (!isOpen || !searchable) {
                return;
            }

            if (enabledOptions.length === 0) {
                setActiveIndex(-1);

                return;
            }

            setActiveIndex(currentIndex => {
                if (currentIndex >= enabledOptions.length || currentIndex < 0) {
                    return 0;
                }

                return currentIndex;
            });
        }, [enabledOptions, inputText, isOpen, searchable]);

        return (
            <div
                ref={containerRef}
                className={cn(styles.selectBox, disabled && styles.selectBox_disabled, className)}
                style={customCss?.wrapper}
            >
                {fieldName ? <input type="hidden" name={fieldName} value={String(selectedValue)} readOnly /> : null}

                {label ? (
                    <label
                        className={cn(styles.selectBox_label, hasError && styles.selectBox_label_error)}
                        htmlFor={selectId}
                    >
                        {label}
                        {required ? <span className={styles.selectBox_required}>*</span> : null}
                    </label>
                ) : null}

                <div
                    className={cn(
                        styles.selectBox_field,
                        sizeClass[size],
                        hasError && styles.selectBox_field_state_error,
                        disabled && styles.selectBox_field_state_disabled,
                        loading && styles.selectBox_field_state_loading,
                        isOpen && !searchable && styles.selectBox_field_state_open,
                        searchable && styles.selectBox_field_type_searchable
                    )}
                    style={customCss?.field}
                >
                    {searchable ? (
                        <>
                            <input
                                ref={ref as Ref<HTMLInputElement>}
                                id={selectId}
                                type="text"
                                role="combobox"
                                className={styles.selectBox_input}
                                value={inputText}
                                disabled={disabled || loading}
                                autoComplete="off"
                                spellCheck={false}
                                aria-haspopup="listbox"
                                aria-expanded={isOpen}
                                aria-controls={listboxId}
                                aria-invalid={hasError}
                                aria-describedby={messageText ? messageId : undefined}
                                onChange={handleInputChange}
                                onFocus={handleInputFocus}
                                onKeyDown={handleInputKeyDown}
                            />

                            {showClearButton ? (
                                <button
                                    type="button"
                                    className={styles.selectBox_clearButton}
                                    aria-label="Очистить выбор"
                                    onMouseDown={handleClearMouseDown}
                                    onClick={handleClear}
                                >
                                    <CloseIcon />
                                </button>
                            ) : null}

                            {loading ? (
                                <span className={styles.selectBox_loader} aria-hidden="true">
                                    <LoadingSpinner />
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    className={cn(
                                        styles.selectBox_chevronButton,
                                        isOpen && styles.selectBox_chevronButton_open
                                    )}
                                    disabled={disabled}
                                    aria-label={isOpen ? 'Закрыть список' : 'Открыть список'}
                                    onMouseDown={handleChevronMouseDown}
                                    onClick={handleToggle}
                                >
                                    <span
                                        className={cn(
                                            styles.selectBox_chevron,
                                            isOpen && styles.selectBox_chevron_open
                                        )}
                                        aria-hidden="true"
                                    >
                                        <ArrowDropDownIcon />
                                    </span>
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            <button
                                ref={ref as Ref<HTMLButtonElement>}
                                id={selectId}
                                type="button"
                                role="combobox"
                                className={styles.selectBox_trigger}
                                disabled={disabled || loading}
                                aria-haspopup="listbox"
                                aria-expanded={isOpen}
                                aria-controls={listboxId}
                                aria-invalid={hasError}
                                aria-describedby={messageText ? messageId : undefined}
                                onClick={handleToggle}
                                onKeyDown={handleTriggerKeyDown}
                            >
                                <span
                                    className={cn(
                                        styles.selectBox_value,
                                        !selectedOption && styles.selectBox_value_placeholder
                                    )}
                                >
                                    {selectedOption ? selectedOption.label : placeholder}
                                </span>
                            </button>

                            {showClearButton ? (
                                <button
                                    type="button"
                                    className={styles.selectBox_clearButton}
                                    aria-label="Очистить выбор"
                                    onMouseDown={handleClearMouseDown}
                                    onClick={handleClear}
                                >
                                    <CloseIcon />
                                </button>
                            ) : null}

                            {loading ? (
                                <span className={styles.selectBox_loader} aria-hidden="true">
                                    <LoadingSpinner />
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    className={cn(
                                        styles.selectBox_chevronButton,
                                        isOpen && styles.selectBox_chevronButton_open
                                    )}
                                    disabled={disabled}
                                    aria-label={isOpen ? 'Закрыть список' : 'Открыть список'}
                                    onMouseDown={handleChevronMouseDown}
                                    onClick={handleToggle}
                                >
                                    <span
                                        className={cn(
                                            styles.selectBox_chevron,
                                            isOpen && styles.selectBox_chevron_open
                                        )}
                                        aria-hidden="true"
                                    >
                                        <ArrowDropDownIcon />
                                    </span>
                                </button>
                            )}
                        </>
                    )}
                </div>

                {isOpen ? (
                    <div className={styles.selectBox_dropdown}>
                        {filteredOptions.length > 0 ? (
                            <ul
                                id={listboxId}
                                role="listbox"
                                className={styles.selectBox_options}
                                aria-labelledby={selectId}
                            >
                                {filteredOptions.map(option => {
                                    const enabledIndex = enabledOptions.findIndex(item => item.value === option.value);
                                    const isSelected = isSameOptionValue(option.value, selectedValue);
                                    const isActive = enabledIndex === activeIndex;

                                    return (
                                        <li
                                            key={option.value}
                                            role="option"
                                            aria-selected={isSelected}
                                            aria-disabled={option.disabled || undefined}
                                            className={cn(
                                                styles.selectBox_option,
                                                isSelected && styles.selectBox_option_selected,
                                                isActive && styles.selectBox_option_active,
                                                option.disabled && styles.selectBox_option_disabled
                                            )}
                                            onMouseEnter={() => {
                                                if (!option.disabled && enabledIndex >= 0) {
                                                    setActiveIndex(enabledIndex);
                                                }
                                            }}
                                            onClick={() => handleSelect(option)}
                                        >
                                            {option.label}
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <div className={styles.selectBox_empty}>{emptySearchText}</div>
                        )}
                    </div>
                ) : null}

                {messageText ? (
                    <div
                        id={messageId}
                        className={cn(styles.selectBox_message, hasError && styles.selectBox_message_error)}
                        style={customCss?.message}
                    >
                        {hasError && errorText ? (
                            <span className={styles.selectBox_errorIcon}>
                                <ErrorCircleIcon />
                            </span>
                        ) : null}
                        <span>{messageText}</span>
                    </div>
                ) : null}
            </div>
        );
    }
);

Select.displayName = 'Select';

export default Select;
